import { Shield, Eye, Database, Lock, UserCheck, FileText, Globe, Mail, Phone, MapPin, Calendar, AlertTriangle, CheckCircle2, FileCheck, Users, CreditCard, Cookie, Bell, Settings } from "lucide-react";

export const PrivacyPolicyContent = () => {
  return (
    <div className="space-y-8">
      {/* Last Updated */}
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        <span>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
      </div>

      {/* Introduction */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Introduction
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            This Privacy Policy describes how Shiftask ("we," "our," or "us") collects, uses, shares, and protects 
            your personal information when you use our human resources management platform, mobile applications, and related services 
            (collectively, the "Services").
          </p>
          <p>
            We are committed to protecting your privacy and ensuring transparency in how we handle your data. This policy 
            complies with the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and other 
            applicable data protection laws.
          </p>
        </div>
      </section>

      {/* PROMINENT DISCLOSURE - Background Location */}
      <section className="bg-yellow-50 dark:bg-yellow-950/20 border-2 border-yellow-500 dark:border-yellow-600 p-6 rounded-lg">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-yellow-900 dark:text-yellow-100">
          <MapPin className="h-6 w-6" />
          IMPORTANT: Background Location Access Disclosure
        </h3>
        <div className="space-y-4 text-sm">
          <div className="bg-yellow-100 dark:bg-yellow-900/30 p-4 rounded-lg border border-yellow-300 dark:border-yellow-700">
            <h4 className="font-bold text-yellow-900 dark:text-yellow-100 mb-2 text-base">
              Why We Need Background Location Access
            </h4>
            <p className="text-yellow-900 dark:text-yellow-100 mb-3">
              Our app requires access to your location <strong>even when the app is closed or not in use</strong> for the following essential workforce management features:
            </p>
            <ul className="space-y-2 text-yellow-900 dark:text-yellow-100">
              <li className="flex items-start gap-2">
                <span className="font-bold mt-1">•</span>
                <span><strong>Automated Check-In/Check-Out:</strong> Automatically clock in when you arrive at your assigned work location and clock out when you leave, ensuring accurate attendance tracking without manual intervention.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold mt-1">•</span>
                <span><strong>Geofencing for Work Sites:</strong> Verify that employees are present at designated work locations during their assigned shifts for security and compliance purposes.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold mt-1">•</span>
                <span><strong>Emergency Response:</strong> Enable rapid location identification for security personnel and emergency responders during critical incidents, even if the app is in the background.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold mt-1">•</span>
                <span><strong>Patrol Route Verification:</strong> Track and verify that security guards complete their assigned patrol routes for client reporting and accountability.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold mt-1">•</span>
                <span><strong>Real-Time Team Coordination:</strong> Allow supervisors to coordinate team members and dispatch personnel to locations as needed during active shifts.</span>
              </li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border-2 border-yellow-500 dark:border-yellow-600">
            <h4 className="font-bold text-foreground mb-2">When Background Location is Collected</h4>
            <p className="text-muted-foreground mb-2">
              Background location data is <strong>ONLY</strong> collected during the following times:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>During your scheduled work shifts when you are on duty</li>
              <li>When you have explicitly clocked in for work through the app</li>
              <li>When emergency response features are activated</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              <strong>Important:</strong> You can disable background location tracking at any time through your device settings, 
              though this may limit certain features like automated check-in/check-out.
            </p>
          </div>

          <div className="bg-red-50 dark:bg-red-950/20 p-4 rounded-lg border border-red-300 dark:border-red-700">
            <h4 className="font-bold text-red-900 dark:text-red-100 mb-2">Your Control Over Location Data</h4>
            <ul className="space-y-2 text-red-900 dark:text-red-100 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>You can revoke location permissions at any time in your device settings</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>You can request deletion of your location history through the app settings</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>You can view all collected location data in your profile</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Location data is encrypted and never sold to third parties</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Data Controller Information */}
      <section className="bg-primary/5 p-4 rounded-lg border-l-4 border-primary">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Data Controller Information
        </h3>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p><strong className="text-foreground">Company Name:</strong> OVIT HR Solutions</p>
          <p><strong className="text-foreground">Service Name:</strong> Shiftask Platform</p>
          <p className="flex items-start gap-2">
            <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span><strong className="text-foreground">Email:</strong> privacy@shiftask.app</span>
          </p>
          <p className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span><strong className="text-foreground">Address:</strong> Athens, Greece, European Union</span>
          </p>
          <p><strong className="text-foreground">Data Protection Officer:</strong> dpo@shiftask.app</p>
        </div>
      </section>

      {/* Information We Collect */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          Information We Collect
        </h3>
        <div className="space-y-4 text-sm">
          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">1. Personal Identification Information</h4>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Full name, email address, phone number</li>
              <li>Date of birth, gender, nationality</li>
              <li>Government-issued identification numbers (as required by law)</li>
              <li>Profile photographs and biographical information</li>
              <li>Employment history and qualifications</li>
            </ul>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">2. Professional Information</h4>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Job title, department, and role within organization</li>
              <li>Work schedule, shifts, and attendance records</li>
              <li>Performance reviews and evaluations</li>
              <li>Training and certification records</li>
              <li>Salary, compensation, and benefits information</li>
            </ul>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">3. Location Data (Including Background Location)</h4>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Precise GPS location when you are on duty and during work shifts</li>
              <li><strong>Background location data</strong> collected even when the app is closed or not in use for:
                <ul className="list-circle list-inside ml-6 mt-1 space-y-1">
                  <li>Automated check-in/check-out at work locations</li>
                  <li>Geofencing and work site verification</li>
                  <li>Emergency response and safety features</li>
                  <li>Patrol route tracking and verification</li>
                  <li>Real-time team coordination</li>
                </ul>
              </li>
              <li>Location timestamps and movement patterns during active shifts</li>
              <li>Geofence entry and exit events</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-2 italic">
              Note: Background location is only collected during scheduled work hours and when you are clocked in. 
              You can disable this in device settings, though it may affect automated features.
            </p>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">4. Technical and Usage Data</h4>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>IP address, device type, and operating system</li>
              <li>Browser type and version</li>
              <li>Login times and session duration</li>
              <li>Pages viewed and features accessed</li>
              <li>Geographic location data (with consent)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">5. Communication Data</h4>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Messages sent through our platform</li>
              <li>Support requests and correspondence</li>
              <li>Feedback and survey responses</li>
              <li>Notifications preferences</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Legal Basis for Processing (GDPR) */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-primary" />
          Legal Basis for Processing (GDPR)
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>We process your personal data based on the following legal grounds:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Contract Performance:</strong> Processing necessary to fulfill our services and employment contracts</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Consent:</strong> Where you have given explicit consent for specific processing activities</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Legal Obligation:</strong> Compliance with employment laws, tax regulations, and legal requirements</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Legitimate Interests:</strong> Business operations, security, fraud prevention, and service improvement</span>
            </li>
          </ul>
        </div>
      </section>

      {/* How We Use Your Data */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          How We Use Your Data
        </h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">Service Delivery:</strong> Provide, maintain, and improve our HR management services</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">User Management:</strong> Create and manage user accounts, authentication, and access control</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">HR Operations:</strong> Employee onboarding, scheduling, attendance tracking, performance management</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">Communication:</strong> Send service updates, notifications, and respond to inquiries</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">Analytics:</strong> Analyze usage patterns to improve features and user experience</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">Security:</strong> Detect, prevent, and address fraud, abuse, and security issues</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span><strong className="text-foreground">Legal Compliance:</strong> Meet regulatory requirements and enforce our terms</span>
          </li>
        </ul>
      </section>

      {/* Data Sharing and Disclosure */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Data Sharing and Disclosure
        </h3>
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">We may share your information with:</p>
          
          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">Within Your Organization</h4>
            <p className="text-muted-foreground">Authorized team members, managers, and administrators with role-based access permissions</p>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">Service Providers</h4>
            <p className="text-muted-foreground">Third-party vendors who assist in operating our services (cloud hosting, analytics, customer support) under strict confidentiality agreements</p>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">Legal Requirements</h4>
            <p className="text-muted-foreground">Law enforcement, regulatory authorities, or legal processes when required by law</p>
          </div>

          <div className="border-l-2 border-primary pl-4">
            <h4 className="font-semibold text-foreground mb-2">Business Transfers</h4>
            <p className="text-muted-foreground">In connection with mergers, acquisitions, or sale of assets (with notice to you)</p>
          </div>

          <div className="bg-destructive/5 p-4 rounded-lg mt-4">
            <p className="text-sm font-medium flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <span>We do NOT sell your personal information to third parties for marketing purposes.</span>
            </p>
          </div>
        </div>
      </section>

      {/* International Data Transfers */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          International Data Transfers
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            Your data may be transferred to and processed in countries outside your jurisdiction. We ensure adequate 
            protection through:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>EU Standard Contractual Clauses (SCCs)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>EU-U.S. Data Privacy Framework participation</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>Adequacy decisions by the European Commission</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Data Security */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-primary" />
          Data Security Measures
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>We implement industry-leading security measures to protect your data:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                Encryption
              </h4>
              <p className="text-xs">TLS 1.3 for data in transit, AES-256 for data at rest</p>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg">
              <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                Access Control
              </h4>
              <p className="text-xs">Role-based permissions, multi-factor authentication</p>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg">
              <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <Eye className="h-4 w-4 text-primary" />
                Monitoring
              </h4>
              <p className="text-xs">24/7 security monitoring and intrusion detection</p>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg">
              <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-primary" />
                Audits
              </h4>
              <p className="text-xs">Regular security audits and penetration testing</p>
            </div>
          </div>
          <div className="bg-primary/5 p-3 rounded-lg mt-4">
            <p className="text-xs">
              <strong className="text-foreground">Certifications:</strong> ISO 27001 (Information Security), 
              ISO 9001 (Quality Management), SOC 2 Type II compliant
            </p>
          </div>
        </div>
      </section>

      {/* Data Retention */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Data Retention
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>We retain your personal data for as long as necessary to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Provide our services and fulfill contractual obligations</li>
            <li>Comply with legal, accounting, or reporting requirements</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Maintain business records for legitimate business purposes</li>
          </ul>
          <p className="mt-3">
            Typical retention periods: Active employment data is retained during employment plus 7 years after termination 
            (or as required by local labor laws). Anonymized analytics data may be retained indefinitely.
          </p>
        </div>
      </section>

      {/* Your Rights (GDPR) */}
      <section className="bg-primary/5 p-4 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-primary" />
          Your Data Protection Rights
        </h3>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">Under GDPR and applicable laws, you have the following rights:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Access</h4>
              <p className="text-xs text-muted-foreground">Request a copy of your personal data we hold</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Rectification</h4>
              <p className="text-xs text-muted-foreground">Correct inaccurate or incomplete information</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Erasure</h4>
              <p className="text-xs text-muted-foreground">Request deletion of your data (right to be forgotten)</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Restriction</h4>
              <p className="text-xs text-muted-foreground">Limit how we process your data</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Portability</h4>
              <p className="text-xs text-muted-foreground">Receive your data in a portable format</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Object</h4>
              <p className="text-xs text-muted-foreground">Object to processing based on legitimate interests</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Withdraw Consent</h4>
              <p className="text-xs text-muted-foreground">Withdraw consent for specific processing activities</p>
            </div>
            <div className="bg-background p-3 rounded-lg border">
              <h4 className="font-semibold text-foreground mb-1">Right to Lodge Complaint</h4>
              <p className="text-xs text-muted-foreground">File a complaint with your data protection authority</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-background rounded-lg border">
            <p className="text-sm">
              <strong className="text-foreground">To exercise your rights:</strong> Contact us at{" "}
              <a href="mailto:privacy@shiftask.app" className="text-primary hover:underline">privacy@shiftask.app</a>
              {" "}or through your account settings. We will respond within 30 days.
            </p>
          </div>
        </div>
      </section>

      {/* Cookies */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Cookie className="h-5 w-5 text-primary" />
          Cookies and Tracking Technologies
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>We use cookies and similar technologies for:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-foreground">Essential Cookies:</strong> Required for service functionality and security</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-foreground">Functional Cookies:</strong> Remember your preferences and settings</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span><strong className="text-foreground">Analytics Cookies:</strong> Understand how you use our services (with consent)</span>
            </li>
          </ul>
          <p className="mt-3">
            You can control cookies through your browser settings. Note that disabling certain cookies may affect 
            service functionality.
          </p>
        </div>
      </section>

      {/* Children's Privacy */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-primary" />
          Children's Privacy
        </h3>
        <div className="bg-destructive/5 p-4 rounded-lg border-l-4 border-destructive">
          <p className="text-sm text-muted-foreground">
            Our services are not intended for individuals under 16 years of age. We do not knowingly collect personal 
            information from children. If you believe we have inadvertently collected data from a child, please contact 
            us immediately at <a href="mailto:privacy@shiftask.app" className="text-primary hover:underline">privacy@shiftask.app</a>.
          </p>
        </div>
      </section>

      {/* Changes to Policy */}
      <section>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Changes to This Privacy Policy
        </h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            We may update this Privacy Policy to reflect changes in our practices or legal requirements. 
            Material changes will be communicated through:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Email notification to registered users</li>
            <li>Prominent notice within our application</li>
            <li>Updated "Last Modified" date at the top of this policy</li>
          </ul>
          <p className="mt-3">
            Continued use of our services after changes constitutes acceptance of the updated policy.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-primary/10 p-6 rounded-lg border border-primary/20">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          Contact Us
        </h3>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            For questions, concerns, or to exercise your data protection rights:
          </p>
          <div className="space-y-2">
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <span><strong className="text-foreground">Privacy Email:</strong> <a href="mailto:privacy@shiftask.app" className="text-primary hover:underline">privacy@shiftask.app</a></span>
            </p>
            <p className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <span><strong className="text-foreground">Data Protection Officer:</strong> <a href="mailto:dpo@shiftask.app" className="text-primary hover:underline">dpo@shiftask.app</a></span>
            </p>
            <p className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <span><strong className="text-foreground">Support:</strong> Available through your account settings</span>
            </p>
            <p className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span><strong className="text-foreground">Address:</strong> OVIT HR Solutions, Athens, Greece, EU</span>
            </p>
          </div>
        </div>
      </section>

      {/* Supervisory Authority */}
      <section className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold text-foreground mb-2">EU Data Protection Supervisory Authority</h4>
        <p className="text-sm text-muted-foreground">
          If you are located in the EU and believe we have not adequately addressed your concerns, you have the right 
          to lodge a complaint with your local data protection authority or the Hellenic Data Protection Authority (Greece).
        </p>
      </section>

      {/* App Store Compliance Notice */}
      <section className="bg-green-50 dark:bg-green-950/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
        <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
          App Store Compliance
        </h4>
        <p className="text-sm text-muted-foreground">
          This privacy policy meets the requirements of the Apple App Store and Google Play Store. Our mobile 
          applications handle data in accordance with this policy and platform-specific guidelines.
        </p>
      </section>
    </div>
  );
};
